import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { auditTime, takeUntil } from 'rxjs/operators';

import type { Form, FormField } from '../../interfaces/form.interface';
import type {
    FilteredValue,
    RepeatableDomainAdapterContext,
    RepeatableGroupConfiguration,
    RepeatableGroupDomainAdapter,
    RepeatableInfoDialogView,
    RepeatableRowExtraView,
    RepeatableSubField,
} from '../../interfaces/input.interface';
import { InputType } from '../../interfaces/input.interface';
import { RepeatableComputeEngine } from './logic/repeatable-compute.engine';
import { RepeatableDomainAdapterFactory } from './logic/repeatable-domain-adapter';
import { RepeatableDropdownResolver } from './logic/repeatable-dropdown.resolver';
import type { PendingDropdownItem, RepeatableLogger, RepeatableRowValue } from './logic/repeatable-group.types';
import { RepeatableModelHydrator } from './logic/repeatable-model-hydrator';
import { RepeatableModelSerializer } from './logic/repeatable-model-serializer';
import { RepeatablePendingDropdownResolver } from './logic/repeatable-pending-dropdown.resolver';
import { RepeatableRowFormFactory } from './logic/repeatable-row-form.factory';
import { RepeatableValueUtils } from './logic/repeatable-value.utils';

@Component({
    selector: 'app-repeatable-group',
    standalone: false,
    templateUrl: './repeatable-group.component.html',
    styleUrls: ['./repeatable-group.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepeatableGroupComponent<T> implements OnInit, OnDestroy, OnChanges {
    @Input({ required: true }) public field: FormField<T> | null = null;
    @Input({ required: true }) public model: FormGroup | null = null;
    @Input({ required: true }) public form: Form<T> | null = null;

    @Input() public isEditable = true;

    /** Enable detailed console diagnostics. */
    @Input() public debug = false;

    public readonly INPUT = InputType;

    public rowsForm: FormGroup = new FormGroup({});
    public rowsArray: FormArray<FormGroup> = new FormArray<FormGroup>([]);

    public infoDialogVisible = false;
    public infoDialogRowIndex: number | null = null;

    private readonly destroyed$ = new Subject<void>();
    private readonly modelBindingsStop$ = new Subject<void>();
    private readonly skipNextModelHydrationControls = new Set<string>();

    private readonly logger: RepeatableLogger = {
        log: (message: string, data?: unknown): void => this.debugLog(message, data),
        warn: (message: string, data?: unknown): void => this.debugWarn(message, data),
    };

    private readonly dropdownResolver = new RepeatableDropdownResolver(
        this.logger,
        () => this.getRowsSnapshotAsStrings(),
        (rowIndex: number) => this.getRowSnapshot(rowIndex),
        () => this.model,
    );

    private readonly modelHydrator = new RepeatableModelHydrator(this.dropdownResolver, this.logger);
    private readonly pendingDropdownResolver = new RepeatablePendingDropdownResolver(this.dropdownResolver, this.logger);
    private readonly computeEngine = new RepeatableComputeEngine(this.logger);
    private readonly modelSerializer = new RepeatableModelSerializer(this.logger);

    private domainAdapter: RepeatableGroupDomainAdapter | null = null;
    private readonly cdr = inject(ChangeDetectorRef);

    /** Avoid infinite loops between model -> rows and rows -> model. */
    private isInternalSync = false;

    /** Prevent writing empty rows into model before server patch arrives. */
    private suspendSyncToModel = true;

    private pendingDropdown: PendingDropdownItem[] = [];
    private isHydratingFromModel = false;
    private pendingResolveTimer: number | null = null;
    private pendingResolveAttempt = 0;

    private get cfg(): RepeatableGroupConfiguration {
        const configuration = this.field?.input?.repeatableGroupConfiguration;

        if (!configuration) {
            this.debugWarn('Repeatable group configuration is missing. Rendering empty safely.');

            return {
                rows: 1,
                addButtonLabel: 'Add',
                bindings: {},
                serialization: { separator: ';' },
                subFields: [],
            };
        }

        return configuration;
    }

    public ngOnInit(): void {
        this.refreshDomainAdapter();
        this.debugLog('ngOnInit() start', {
            isEditable: this.isEditable,
            bindings: this.cfg.bindings,
            serialization: this.cfg.serialization,
            subFields: (this.cfg.subFields ?? []).map((subField) => subField.key),
        });

        this.bootstrapFormSafe();
        this.bindModelHydrationPipeline();
        this.hydrateFromModel();
        this.bindRecomputePipeline();

        this.debugLog('ngOnInit() done');
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['model'] && !changes['model'].firstChange) {
            this.debugWarn('model reference changed -> rebind + rehydrate');
            this.bindModelHydrationPipeline();
            this.hydrateFromModel();
        }

        if (changes['field'] && !changes['field'].firstChange) {
            this.debugWarn('field changed -> rehydrate');
            this.refreshDomainAdapter();
            this.hydrateFromModel();
        }
    }

    public ngOnDestroy(): void {
        this.debugLog('ngOnDestroy()');
        this.clearPendingResolveTimer();
        this.modelBindingsStop$.next();
        this.modelBindingsStop$.complete();
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    public visibleSubFields(): RepeatableSubField[] {
        return (this.cfg.subFields ?? []).filter((subField) => this.safeIsVisible(subField));
    }

    public trackByIndex(index: number): number {
        return index;
    }

    public trackBySubFieldKey(index: number, subField: RepeatableSubField): string {
        return subField.key ?? String(index);
    }

    public get currentCount(): number {
        return this.rowsArray?.length ?? 0;
    }

    public get maxRows(): number {
        const max = Number(this.cfg.rows ?? 1);

        return Number.isFinite(max) && max > 0 ? max : 1;
    }

    public canAddRow(): boolean {
        return this.isEditable && this.currentCount < this.maxRows;
    }

    public canDeleteRow(): boolean {
        return this.isEditable && this.currentCount > 1;
    }

    public getRowGroup(rowIndex: number): FormGroup {
        return this.rowsArray.at(rowIndex);
    }

    public addRow(): void {
        if (!this.canAddRow()) {
            return;
        }

        this.debugLog('addRow() before', { length: this.rowsArray.length });
        this.rowsArray.push(RepeatableRowFormFactory.createRowGroup(this.cfg.subFields ?? []));
        this.enableSyncIfUserEditing();
        this.recomputeAllAndSync('addRow');
        this.debugLog('addRow() after', { length: this.rowsArray.length });
    }

    public deleteRow(rowIndex: number): void {
        if (!this.isEditable || rowIndex < 0 || rowIndex >= this.currentCount) {
            return;
        }

        this.debugLog('deleteRow()', { rowIndex, beforeLength: this.rowsArray.length });
        this.rowsArray.removeAt(rowIndex);

        if (this.rowsArray.length === 0) {
            this.rowsArray.push(RepeatableRowFormFactory.createRowGroup(this.cfg.subFields ?? []));
        }

        this.enableSyncIfUserEditing();
        this.recomputeAllAndSync('deleteRow');
        this.debugLog('deleteRow() done', { afterLength: this.rowsArray.length });
    }

    public onlyTextCellText(subField: RepeatableSubField, rowIndex: number): string {
        const value = this.rowsArray.at(rowIndex)?.get(subField.key)?.value;

        if (value === null || value === undefined) {
            return '0,00';
        }

        const normalizedValue = String(value).trim();

        return normalizedValue === '' || RepeatableValueUtils.isNullishStringToken(normalizedValue) ? '0,00' : normalizedValue;
    }

    public readOnlyCellText(subField: RepeatableSubField, rowIndex: number): string {
        const value = this.rowsArray.at(rowIndex)?.get(subField.key)?.value;

        if (value === null || value === undefined) {
            return subField.input?.type === InputType.ONLY_TEXT ? '0,00' : '-';
        }

        if (typeof value === 'string') {
            const trimmedValue = value.trim();

            if (trimmedValue === '' || RepeatableValueUtils.isNullishStringToken(trimmedValue)) {
                return subField.input?.type === InputType.ONLY_TEXT ? '0,00' : '-';
            }

            if (this.isInfoLabelValue(subField, trimmedValue, rowIndex)) {
                return RepeatableValueUtils.normalizeLabelText(trimmedValue) || '-';
            }
        }

        switch (subField.input?.type) {
            case InputType.DROPDOWN:
            case InputType.DROPDOWN_SEARCH:
                return this.resolveDropdownLabel(subField, value, rowIndex) ?? '-';

            case InputType.MULTISELECT:
            case InputType.MULTISELECT_SEARCH:
                return this.resolveMultiLabels(subField, value, rowIndex) ?? '-';

            default:
                return String(value);
        }
    }

    public getNumericInputClasses(subField: RepeatableSubField, rowIndex: number): Record<string, boolean> {
        return this.getDomainAdapter().getNumericInputClasses?.(this.createDomainContext(rowIndex), subField.key) ?? {};
    }

    public rowExtraView(rowIndex: number): RepeatableRowExtraView {
        return this.getDomainAdapter().getRowExtraView?.(this.createDomainContext(rowIndex)) ?? {
            isVisible: false,
            items: [],
        };
    }

    public shouldShowRowExtra(rowIndex: number): boolean {
        return this.rowExtraView(rowIndex).isVisible;
    }

    public canShowInfoDialog(rowIndex: number): boolean {
        return this.getDomainAdapter().canOpenInfoDialog?.(this.createDomainContext(rowIndex)) ?? false;
    }

    public canShowRowActions(rowIndex: number): boolean {
        return this.isEditable || this.canShowInfoDialog(rowIndex);
    }

    public openInfoDialog(rowIndex: number): void {
        if (!this.canShowInfoDialog(rowIndex)) {
            return;
        }

        this.infoDialogRowIndex = rowIndex;
        this.infoDialogVisible = true;
    }

    public closeInfoDialog(): void {
        this.infoDialogVisible = false;
        this.infoDialogRowIndex = null;
    }

    public onInfoDialogVisibleChange(visible: boolean): void {
        if (!visible) {
            this.closeInfoDialog();
            return;
        }

        this.infoDialogVisible = true;
    }

    public get activeInfoDialogView(): RepeatableInfoDialogView | null {
        if (this.infoDialogRowIndex === null || this.infoDialogRowIndex < 0) {
            return null;
        }

        return this.getDomainAdapter().getInfoDialogView?.(this.createDomainContext(this.infoDialogRowIndex)) ?? null;
    }

    public debugDump(): void {
        if (!this.debug) {
            return;
        }

        this.debugLog('debugDump()', {
            isEditable: this.isEditable,
            maxRows: this.maxRows,
            suspendSyncToModel: this.suspendSyncToModel,
            rows: this.getRowsSnapshot(),
            pendingDropdown: this.pendingDropdown,
            modelValue: this.model?.getRawValue?.() ?? null,
        });
    }

    public dropdownOptions(subField: RepeatableSubField, rowIndex: number): Array<FilteredValue<unknown>> {
        return this.dropdownResolver.options(subField, rowIndex);
    }

    public forceRehydrate(): void {
        this.hydrateFromModel();
        this.cdr.markForCheck();
    }

    private refreshDomainAdapter(): void {
        this.domainAdapter = RepeatableDomainAdapterFactory.create(this.cfg);
    }

    private getDomainAdapter(): RepeatableGroupDomainAdapter {
        if (!this.domainAdapter) {
            this.refreshDomainAdapter();
        }

        return this.domainAdapter as RepeatableGroupDomainAdapter;
    }

    private bootstrapFormSafe(): void {
        const rowsState = RepeatableRowFormFactory.createRowsForm(this.cfg.subFields ?? []);

        this.rowsForm = rowsState.rowsForm;
        this.rowsArray = rowsState.rowsArray;
    }

    private bindModelHydrationPipeline(): void {
        if (!this.model) {
            this.debugWarn('bindModelHydrationPipeline(): model missing.');
            return;
        }

        const entries = Object.entries(this.cfg.bindings ?? {}) as Array<[string, string]>;

        if (entries.length === 0) {
            return;
        }

        this.modelBindingsStop$.next();

        for (const [logicalKey, targetControlName] of entries) {
            const control = this.model.get(targetControlName);

            if (!control) {
                this.debugWarn('Model control missing for binding.', { logicalKey, targetControlName });
                continue;
            }

            control.valueChanges
                .pipe(auditTime(0), takeUntil(this.destroyed$), takeUntil(this.modelBindingsStop$))
                .subscribe((value: unknown) => this.handleBoundModelChange(targetControlName, value));
        }

        if (this.modelSerializer.isAnyBindingNonEmpty(this.model, entries)) {
            this.suspendSyncToModel = false;
            this.debugLog('Model already ready at init -> enabling sync to model.');
        }
    }

    private handleBoundModelChange(targetControlName: string, value: unknown): void {
        if (this.skipNextModelHydrationControls.delete(targetControlName)) {
            this.debugLog('Ignored internal model echo.', { targetControlName, value });
            return;
        }

        if (this.isInternalSync || this.isHydratingFromModel) {
            return;
        }

        if (this.suspendSyncToModel && RepeatableValueUtils.hasNonEmptyValue(value)) {
            this.suspendSyncToModel = false;
            this.debugLog('Model became ready -> enabling sync to model.', { targetControlName, value });
        }

        this.debugLog('External model change -> hydrateFromModel()', { targetControlName, value });
        this.hydrateFromModel();
    }

    private bindRecomputePipeline(): void {
        this.rowsArray.valueChanges
            .pipe(auditTime(0), takeUntil(this.destroyed$))
            .subscribe(() => {
                if (this.isInternalSync) {
                    return;
                }

                if (this.pendingDropdown.length > 0) {
                    this.debugWarn(
                        'rowsArray.valueChanges: dropping pendingDropdown because user edited a row.',
                        { droppedCount: this.pendingDropdown.length, pending: this.pendingDropdown }
                    );
                    this.pendingDropdown = [];
                    this.clearPendingResolveTimer();
                }

                this.enableSyncIfUserEditing();
                this.debugLog('rowsArray.valueChanges -> recomputeAllAndSync()', this.getRowsSnapshot());
                this.recomputeAllAndSync('rowsArray.valueChanges');
            });
    }

    private enableSyncIfUserEditing(): void {
        if (!this.isEditable) {
            return;
        }

        if (this.suspendSyncToModel) {
            this.suspendSyncToModel = false;
            this.debugWarn('Sync to model enabled due to user editing before model became ready.');
        }
    }

    private hydrateFromModel(): void {
        this.pendingDropdown = [];
        this.pendingResolveAttempt = 0;
        this.isHydratingFromModel = true;
        this.isInternalSync = true;

        try {
            const result = this.modelHydrator.hydrateFromModel({
                model: this.model,
                rowsArray: this.rowsArray,
                configuration: this.cfg,
                isEditable: this.isEditable,
                maxRows: this.maxRows,
            });

            this.pendingDropdown = result.pendingDropdown;
        } finally {
            this.isInternalSync = false;
            this.isHydratingFromModel = false;
        }

        this.recomputeAll('hydrateFromModel');
        this.syncRowsValidationToModel();
        this.cdr.markForCheck();

        if (this.pendingDropdown.length > 0) {
            this.debugWarn('hydrateFromModel(): pendingDropdown (options not ready or ID filtered out).', {
                pendingCount: this.pendingDropdown.length,
                pending: this.pendingDropdown,
            });
            this.scheduleResolvePending();
            return;
        }

        this.syncRowsToModel('hydrateFromModel');
    }

    private recomputeAllAndSync(source: string): void {
        this.recomputeAll(source);
        this.syncRowsValidationToModel();
        this.syncRowsToModel(source);
    }

    private recomputeAll(source: string): void {
        this.computeEngine.recomputeAll(source, this.rowsArray, this.cfg.subFields ?? [], this.model);
    }

    private syncRowsValidationToModel(): void {
        const fieldControlName = this.field?.name ?? '';
        const fieldControl = fieldControlName ? this.model?.get(fieldControlName) : null;

        if (!fieldControl) {
            return;
        }

        const currentErrors = { ...(fieldControl.errors ?? {}) };

        delete currentErrors['repeatableRowInvalid'];

        if (this.rowsArray.invalid) {
            currentErrors['repeatableRowInvalid'] = 'El grupo contiene valores inválidos. Revisa las filas marcadas.';
        }

        const nextErrors = Object.keys(currentErrors).length > 0 ? currentErrors : null;

        fieldControl.setErrors(nextErrors);
    }

    private syncRowsToModel(source: string): void {
        this.isInternalSync = true;

        try {
            this.modelSerializer.syncRowsToModel({
                source,
                model: this.model,
                rowsArray: this.rowsArray,
                configuration: this.cfg,
                isEditable: this.isEditable,
                isHydratingFromModel: this.isHydratingFromModel,
                hasPendingDropdowns: this.pendingDropdown.length > 0,
                suspendSyncToModel: this.suspendSyncToModel,
                skipNextModelHydrationControls: this.skipNextModelHydrationControls,
            });
        } finally {
            this.isInternalSync = false;
        }
    }

    private scheduleResolvePending(): void {
        this.clearPendingResolveTimer();

        this.pendingResolveTimer = window.setTimeout(() => {
            this.tryResolvePending();
        }, 50);

        this.debugLog('scheduleResolvePending() setTimeout 50ms');
    }

    private tryResolvePending(): void {
        if (this.pendingDropdown.length === 0) {
            return;
        }

        const maxAttempts = 200;

        this.pendingResolveAttempt++;

        const result = this.pendingDropdownResolver.resolvePending(this.rowsArray, this.pendingDropdown);

        this.pendingDropdown = result.stillPending;

        this.debugLog('tryResolvePending(): status', {
            attempt: this.pendingResolveAttempt,
            stillPendingCount: this.pendingDropdown.length,
            stillPending: this.pendingDropdown,
            didResolveAny: result.didResolveAny,
            rows: this.getRowsSnapshot(),
            modelValue: this.model?.getRawValue?.() ?? null,
        });

        if (this.pendingDropdown.length > 0 && this.pendingResolveAttempt < maxAttempts) {
            this.scheduleResolvePending();
            return;
        }

        this.recomputeAll('tryResolvePending');

        if (this.pendingDropdown.length > 0) {
            this.debugWarn('tryResolvePending(): unresolved dropdowns remain after max attempts. Keeping model untouched.', {
                pendingCount: this.pendingDropdown.length,
                pending: this.pendingDropdown,
            });
            return;
        }

        this.syncRowsToModel('tryResolvePending');
    }

    private clearPendingResolveTimer(): void {
        if (this.pendingResolveTimer !== null) {
            clearTimeout(this.pendingResolveTimer);
            this.pendingResolveTimer = null;
        }
    }

    private createDomainContext(rowIndex: number): RepeatableDomainAdapterContext {
        return {
            rowIndex,
            row: this.getRowSnapshot(rowIndex),
            rows: this.getRowsSnapshotAsStrings(),
            model: this.model,
            subFields: this.cfg.subFields ?? [],
            resolveEntity: <TEntity extends Record<string, unknown>>(key: string): TEntity | null =>
                this.resolveSelectedEntity<TEntity>(rowIndex, key),
        };
    }

    private resolveSelectedEntity<TEntity extends Record<string, unknown>>(rowIndex: number, key: string): TEntity | null {
        const row = this.getRowSnapshot(rowIndex);
        const value = row[key];

        if (value !== null && value !== undefined && typeof value === 'object' && !Array.isArray(value)) {
            return value as TEntity;
        }

        const subField = (this.cfg.subFields ?? []).find((item) => item.key === key);

        if (!subField) {
            return null;
        }

        const resolvedValue = this.dropdownResolver.mapIdToDropdownValue(value, subField, rowIndex);

        if (resolvedValue !== null && typeof resolvedValue === 'object' && !Array.isArray(resolvedValue)) {
            return resolvedValue as TEntity;
        }

        return null;
    }

    private isInfoLabelValue(subField: RepeatableSubField, raw: unknown, rowIndex: number): boolean {
        if (this.isEditable) {
            return false;
        }

        const type = subField.input?.type;
        const isSelect = RepeatableValueUtils.isSelectType(type);

        if (!isSelect || raw === null || raw === undefined || typeof raw === 'object') {
            return false;
        }

        const stringValue = String(raw).trim();

        if (stringValue === '' || RepeatableValueUtils.isNullishStringToken(stringValue)) {
            return false;
        }

        const parts = stringValue
            .split(';')
            .map((part) => part.trim())
            .filter((part) => part !== '');

        if (parts.length === 0 || parts.every((part) => RepeatableValueUtils.isNumericLike(part))) {
            return false;
        }

        const primaryKey = subField.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
        const options = this.dropdownOptions(subField, rowIndex);

        if (options.length > 0) {
            return !parts.some((part) => options.some((option) => RepeatableValueUtils.getOptionKey(option?.value, primaryKey) === part));
        }

        return true;
    }

    private resolveDropdownLabel(subField: RepeatableSubField, raw: unknown, rowIndex: number): string | null {
        const primaryKey = subField.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
        const ids = RepeatableValueUtils.parseIdsFromToken(raw, ';', primaryKey);

        if (ids.length === 0) {
            return null;
        }

        const labels = this.dropdownResolver.idsToLabels(subField, ids, rowIndex);

        return labels.length > 0 ? labels.join(', ') : null;
    }

    private resolveMultiLabels(subField: RepeatableSubField, raw: unknown, rowIndex: number): string | null {
        const primaryKey = subField.input?.dropdownConfiguration?.valuePrimaryKey ?? 'id';
        const ids = RepeatableValueUtils.parseIdsFromToken(raw, ';', primaryKey);

        if (ids.length === 0) {
            return null;
        }

        const labels = this.dropdownResolver.idsToLabels(subField, ids, rowIndex);

        return labels.length > 0 ? labels.join(', ') : null;
    }

    private safeIsVisible(subField: RepeatableSubField): boolean {
        if (typeof subField.isVisible !== 'function') {
            return true;
        }

        try {
            return subField.isVisible() !== false;
        } catch (error: unknown) {
            this.debugWarn('isVisible() threw; showing field by default.', { key: subField.key, error });
            return true;
        }
    }

    private getRowSnapshot(rowIndex: number): RepeatableRowValue {
        return (this.rowsArray.at(rowIndex)?.getRawValue() as RepeatableRowValue | undefined) ?? {};
    }

    private getRowsSnapshot(): RepeatableRowValue[] {
        return RepeatableValueUtils.getRowsSnapshot(this.rowsArray);
    }

    private getRowsSnapshotAsStrings(): Array<Record<string, string>> {
        return RepeatableValueUtils.getRowsSnapshotAsStrings(this.rowsArray, this.cfg.subFields ?? []);
    }

    private debugLog(message: string, data?: unknown): void {
        if (!this.debug) {
            return;
        }

        console.log(`[RepeatableGroup] ${message}`, data ?? '');
    }

    private debugWarn(message: string, data?: unknown): void {
        if (!this.debug) {
            return;
        }

        console.warn(`[RepeatableGroup] ${message}`, data ?? '');
    }
}
